import { Avatar, Button, Grid, makeStyles, Theme } from "@material-ui/core";
import React, { useState } from "react";
import { RecordHeader } from "@ofqual/portal-components/components/RecordHeader";
import { NonNullableItemState } from "../../redux/state";
import { useAuth, useLookup } from "@ofqual/portal-core/core/hooks";
import { authSelector } from "../../redux/selectors";
import { UserType } from "../../redux/types";
import { SocStatus } from "../../domain/SocItem";
import { AssignDialog } from "./activityFeed/dialogs/AssignDialog";
import { socStatusService } from "../../services";

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridItem: {
      borderRight: "1px solid rgba(255,255,255,0.2)",
      padding: "0 15px",
      "& div": {
        padding: "5px",
      },
    },
  };
});

type ItemHeaderProps = {
  state: NonNullableItemState | null;
};

export const ItemHeader: React.FC<ItemHeaderProps> = ({ state }) => {
  const classes = useStyles();
  const { userType } = useAuth(authSelector);

  const canAssign = userType === UserType.Ofqual;
  const [isDialogOpen, setIsOpenAssignDialog] = useState(false);

  const [socStatusLookups] = useLookup(socStatusService);

  return (
    <>
      <RecordHeader>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="stretch"
          spacing={2}
          style={{ height: "100%" }}
        >
          <Grid className={classes.gridItem}>
            <div>
              <label>
                <strong>Reference:</strong>
              </label>{" "}
              <span data-test="metadata-acronym">
                {state?.item?.referenceNumber}
              </span>
            </div>
            <div>
              <label>
                <strong>Status:</strong>
              </label>{" "}
              <span data-test="metadata-status">
                {userType === UserType.Ofqual
                  ? socStatusLookups.find((x) => x.id === state?.item?.status)
                      ?.label ?? "Unknown"
                  : state?.item?.status !== SocStatus.InReview &&
                    state?.item?.status !== SocStatus.ReviewMeetingHeld
                  ? socStatusLookups.find((x) => x.id === state?.item?.status)
                      ?.label ?? "Unknown"
                  : SocStatus[SocStatus.Submitted]}
              </span>
            </div>
          </Grid>
          {canAssign && (
            <Grid className={classes.gridItem}>
              <Button
                onClick={() => setIsOpenAssignDialog(true)}
                data-test="soc-assigned-to"
                style={{ color: "#f5f5f5" }}
              >
                {renderAssignButtonContent(
                  state?.item.assignedToDisplayName || "",
                  state?.item.assignedToUpn || ""
                )}
              </Button>
            </Grid>
          )}
          <Grid className={classes.gridItem}>
            <div>
              <label>
                <strong>Awarding organisation:</strong>
              </label>{" "}
              <span data-test="metadata-acronym">
                {state?.item?.orgName}
              </span>
            </div>
          </Grid>
        </Grid>
      </RecordHeader>
      <AssignDialog
        soc={state?.item!}
        onClose={() => setIsOpenAssignDialog(false)}
        open={isDialogOpen}
        userType={userType}
        allowReassign={state?.item.status !== SocStatus.Closed}
      />
    </>
  );
};

const renderAssignButtonContent = (
  assignedDisplayName?: string,
  assignedUpn?: string
) => {
  const calculateInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("");
  };
  return (
    <>
      <Avatar
        style={{
          backgroundColor: "#293445",
          width: 70,
          height: 70,
          fontSize: 30,
        }}
      >
        {assignedDisplayName ? calculateInitials(assignedDisplayName) : ""}
      </Avatar>
      {assignedUpn ? (
        <div>
          <label>
            <strong>Case lead:</strong>
          </label>
          <br></br>
          <span data-test="metadata-assigned-to">
            {assignedDisplayName
              ? assignedDisplayName
              : assignedUpn
              ? assignedUpn
              : "Case unassigned"}
          </span>
        </div>
      ) : (
        <div>
          <label>
            <strong>Assign case to</strong>
          </label>
        </div>
      )}
    </>
  );
};
